import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, EmptyState } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Users, CalendarDays, Dumbbell, CheckCircle2, Library, DollarSign,
  UserPlus, Plus, Send, Clock, AlertTriangle, Loader2, UserCheck,
} from "lucide-react";

export default function TherapistDashboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [feed, setFeed] = useState([]);
  const [plans, setPlans] = useState([]);
  const [paying, setPaying] = useState(false);
  const [todayAppts, setTodayAppts] = useState([]);
  const [sub, setSub] = useState(null);
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);

  useEffect(() => {
    api.get("/therapist/stats").then(({ data }) => setS(data)).catch(() => {});
    api.get("/therapist/activity").then(({ data }) => setFeed(data)).catch(() => {});
    api.get("/plans").then(({ data }) => setPlans(data.filter((p) => p.active)));
    api.get("/appointments").then(({ data }) => setTodayAppts(
      data.filter((a) => a.date === todayStr && ["confirmed", "rescheduled", "requested"].includes(a.status))
          .sort((x, y) => (x.time || "").localeCompare(y.time || ""))
    )).catch(() => {});
    api.get("/subscription/me").then(({ data }) => setSub(data)).catch(() => {});
  }, []);

  const loadSub = () => api.get("/subscription/me").then(({ data }) => setSub(data)).catch(() => {});
  const cancelSub = async () => {
    try { await api.post("/subscription/cancel"); toast.success("Assinatura será cancelada ao fim do período"); loadSub(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao cancelar"); }
  };
  const reactivateSub = async () => {
    try { await api.post("/subscription/reactivate"); toast.success("Assinatura reativada"); loadSub(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao reativar"); }
  };

  const subscribe = async (planId) => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/subscribe", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      setPaying(false);
    }
  };

  if (user?.status && user.status !== "active") {
    const needsPayment = user.payment_status !== "paid";
    return (
      <div>
        <PageHeader title={`Olá, ${user.name}`} />
        <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-900/20 mb-6">
          <Clock className="w-4 h-4" />
          <AlertTitle>Conta {user.status === "pending" ? "pendente de aprovação" : user.status}</AlertTitle>
          <AlertDescription>
            {user.status === "pending"
              ? (needsPayment
                  ? "Para ativar seu acesso, assine um plano abaixo. Após o pagamento, seu cadastro será enviado para aprovação do administrador."
                  : "Assinatura confirmada! Seu cadastro está aguardando aprovação do administrador. Você será notificado assim que for aprovado.")
              : "Sua conta não está ativa. Entre em contato com o administrador."}
          </AlertDescription>
        </Alert>

        {needsPayment && (
          <div className="grid md:grid-cols-3 gap-4">
            {plans.map((p) => (
              <Card key={p.id} className="p-6 flex flex-col" data-testid={`sub-plan-${p.name.toLowerCase()}`}>
                <h3 className="font-heading text-xl font-medium">{p.name}</h3>
                <div className="mt-2 font-heading text-3xl font-semibold text-primary">R$ {p.price.toFixed(2)}<span className="text-sm text-muted-foreground font-normal">/mês</span></div>
                <ul className="mt-4 space-y-1.5 flex-1 text-sm text-muted-foreground">
                  {p.features.map((f, i) => <li key={i}>• {f}</li>)}
                </ul>
                <Button className="rounded-full mt-5" disabled={paying} onClick={() => subscribe(p.id)} data-testid={`subscribe-${p.name.toLowerCase()}`}>
                  {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assinar plano"}
                </Button>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  const shortcuts = [
    { label: "Adicionar cliente", icon: UserPlus, to: "/painel/clientes", testid: "sc-add-client" },
    { label: "Novo exercício", icon: Plus, to: "/painel/exercicios", testid: "sc-new-exercise" },
    { label: "Recomendar conteúdo", icon: Send, to: "/painel/conteudos", testid: "sc-recommend" },
    { label: "Ver agenda", icon: CalendarDays, to: "/painel/agenda", testid: "sc-agenda" },
    { label: "Configurar valores", icon: DollarSign, to: "/painel/valores", testid: "sc-values" },
  ];

  return (
    <div>
      <PageHeader title={`Olá, ${user?.name}`} subtitle="Acompanhe seus clientes entre as sessões" />

      <div className="flex flex-wrap gap-2 mb-6">
        {shortcuts.map((sc) => (
          <Button key={sc.label} variant="outline" className="rounded-full" onClick={() => nav(sc.to)} data-testid={sc.testid}>
            <sc.icon className="w-4 h-4 mr-2" />{sc.label}
          </Button>
        ))}
      </div>

      {sub?.has_subscription && (
        <Card className="p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="subscription-card">
          <div>
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              <span className="font-medium">Assinatura mensal{sub.plan ? ` — ${sub.plan.name}` : ""}</span>
              {sub.subscription_status === "active" && !sub.cancel_at_period_end && <span className="text-xs px-2 py-0.5 rounded-full bg-accent/15 text-accent font-medium">Ativa</span>}
              {sub.cancel_at_period_end && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Cancela ao fim do período</span>}
              {sub.subscription_status === "past_due" && <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Pagamento pendente</span>}
              {sub.subscription_status === "canceled" && <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground font-medium">Cancelada</span>}
            </div>
            {sub.plan && <p className="text-sm text-muted-foreground mt-1">R$ {sub.plan.price.toFixed(2)}/mês · cobrança recorrente via Stripe</p>}
          </div>
          {sub.cancel_at_period_end || sub.subscription_status === "canceled"
            ? <Button variant="outline" className="rounded-full" onClick={reactivateSub} data-testid="reactivate-sub-btn">Reativar assinatura</Button>
            : <Button variant="outline" className="rounded-full text-destructive" onClick={cancelSub} data-testid="cancel-sub-btn">Cancelar assinatura</Button>}
        </Card>
      )}

      {!s ? <div className="h-32 animate-pulse bg-secondary rounded-2xl" /> : (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <StatCard icon={Users} label="Total de clientes" value={s.clients} />
          <StatCard icon={CheckCircle2} label="Clientes ativos" value={s.active_clients} accent="accent" />
          <StatCard icon={CalendarDays} label="Próximas consultas" value={s.upcoming_appointments} accent="amber" />
          <StatCard icon={Dumbbell} label="Exercícios pendentes" value={s.pending_exercises} accent="amber" />
          <StatCard icon={CheckCircle2} label="Exercícios concluídos" value={s.completed_exercises} accent="accent" />
          <StatCard icon={Library} label="Recomendações" value={s.recommendations} accent="slate" />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <Card className="p-6 lg:col-span-2">
          <h3 className="font-heading text-lg font-medium mb-4">Atividades recentes dos clientes</h3>
          {feed.length === 0 ? (
            <EmptyState icon={Users} title="Nenhum cliente ainda" desc="Adicione seu primeiro cliente para começar o acompanhamento."
              action={<Button onClick={() => nav("/painel/clientes")} className="rounded-full"><UserPlus className="w-4 h-4 mr-2" />Adicionar cliente</Button>} />
          ) : (
            <div className="space-y-4">
              {feed.map((c) => (
                <div key={c.client_id} className="flex items-center gap-4" data-testid={`activity-${c.client_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium truncate">{c.name}</span>
                      <span className="text-sm text-muted-foreground shrink-0">
                        {c.pending > 0 ? `${c.pending} pendente(s)` : c.completed > 0 ? "100% concluído" : "Sem exercícios"}
                      </span>
                    </div>
                    <Progress value={c.progress} className="h-2" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6" data-testid="presence-panel">
          <h3 className="font-heading text-lg font-medium mb-1">Presenças de hoje</h3>
          <p className="text-sm text-muted-foreground mb-4">{today.toLocaleDateString("pt-BR")}</p>
          {todayAppts.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Nenhuma consulta agendada para hoje.</p>
          ) : (
            <div className="space-y-3">
              {todayAppts.map((a) => (
                <div key={a.id} className="flex items-center gap-3" data-testid={`presence-${a.id}`}>
                  <div className="font-heading font-semibold text-primary w-12 shrink-0">{a.time}</div>
                  <div className="flex-1 min-w-0"><div className="font-medium truncate text-sm">{a.client_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{a.type}</div></div>
                  {a.attendance_confirmed
                    ? <span className="inline-flex items-center gap-1 text-xs text-accent font-medium shrink-0"><UserCheck className="w-3.5 h-3.5" />Confirmada</span>
                    : <span className="text-xs text-muted-foreground shrink-0">Aguardando</span>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
